import { http } from '@/helpers/easyindustriaapi/config'
import { userHasPermission } from '@/helpers/userPermissions'
import { hasFiscalBlockingErrors, validOperationVersion } from '@/modules/nfse/application/nfseWorkflowContract'

const fiscalErrorCode = body => body?.code || body?.errors?.code || body?.data?.code
  || body?.blocking_errors?.[0]?.code || body?.data?.blocking_errors?.[0]?.code
const fiscalErrorMessage = (body, fallback) => fiscalErrorCode(body) === 'fiscal_configuration_unavailable'
  ? 'Configuração fiscal indisponível para esta empresa.'
  : (typeof body?.message === 'string' ? body.message : fallback)
const objectEntries = value => Object.keys(value || {}).map(key => [key, value[key]])
const unsupportedStatuses = [404, 405, 501]
const fiscalFieldErrors = errors => objectEntries(errors)
  .filter(([field, messages]) => field !== 'code' && Array.isArray(messages))
  .reduce((normalized, [field, messages]) => {
    const validMessages = messages.filter(message => typeof message === 'string')
    if (validMessages.length) normalized[field] = validMessages
    return normalized
  }, {})

// Adaptador dos endpoints PROPOSTOS nas Specs. Não conhece o contrato do provedor.
// Ausência/incompatibilidade da API nunca aciona os escritores fiscais legados.
export default class NfseController {
  constructor(user, empresa, client = http) {
    this.user = user
    this.empresa = empresa
    this.http = client
  }

  async request(method, path, permission, payload = {}, fiscalMutation = false, allowNullData = false) {
    if (!this.user?.id || !this.empresa?.id || !userHasPermission(this.user, permission)) {
      return { success: false, message: 'Empresa ou permissão fiscal não autorizada.', status: 403 }
    }
    const config = { headers: { 'X-Empresa-Id': String(this.empresa.id) }, nfseRequest: true }
    try {
      const response = method === 'get'
        ? await this.http.get(path, { ...config, params: payload })
        : method === 'put' ? await this.http.put(path, payload, config) : await this.http.post(path, payload, config)
      const body = response.data
      if (unsupportedStatuses.indexOf(response.status) !== -1) return this.unconfirmed(path, response.status)
      if (response.status < 200 || response.status >= 300 || body?.success !== true || hasFiscalBlockingErrors(body) || hasFiscalBlockingErrors(body?.data)) {
        return { success: false, code: fiscalErrorCode(body) || 'nfse_contract_error', status: response.status, uncertain: fiscalMutation && (response.status >= 500 || typeof body?.success !== 'boolean'), message: fiscalErrorMessage(body, 'Resposta inválida ou operação fiscal não confirmada pela API.'), errors: fiscalFieldErrors(body?.errors) }
      }
      // A Spec funcional também propõe a resposta assíncrona sem data.
      let data = body.data ?? (body.operation_id ? { operation_id: body.operation_id, status: body.status, groups: body.groups } : null)
      if (data == null && allowNullData) return { success: true, data: null, status: response.status, message: typeof body.message === 'string' ? body.message : '' }
      if (data == null || typeof data !== 'object') return { success: false, uncertain: fiscalMutation, message: 'Contrato de resposta não confirmado: dados ausentes.' }
      if (!Array.isArray(data) && body.operation_id && !data.operation_id) data = { ...data, operation_id: body.operation_id }
      // HTTP 202 confirma somente aceitação durável; nem mesmo status incoerente autoriza a nota.
      if (response.status === 202) {
        const fallback = path.slice(-9) === '/cancelar' ? 'cancel_requested' : 'processing'
        const state = data.status === 'unknown' ? 'unknown' : (['processing', 'cancel_requested'].indexOf(data.status) !== -1 ? data.status : fallback)
        data = { ...data, status: state, groups: (Array.isArray(data.groups) ? data.groups : []).map(group => ({ ...group, status: group.status === 'unknown' ? 'unknown' : state })) }
      }
      return { success: true, data, status: response.status, message: typeof body.message === 'string' ? body.message : '' }
    } catch (error) {
      const status = error.response?.status
      if (unsupportedStatuses.indexOf(status) !== -1) return this.unconfirmed(path, status)
      const uncertain = fiscalMutation && (!status || status >= 500 || status === 408)
      const responseData = error.response?.data || {}
      const code = fiscalErrorCode(responseData) || 'nfse_request_failed'
      const responseMessage = fiscalErrorMessage(responseData, 'Não foi possível consultar o serviço fiscal.')
      return { success: false, code, status: status || 0, uncertain, message: uncertain
        ? 'Resultado desconhecido. Consulte a operação para reconciliação; não reenvie a emissão ou o cancelamento.'
        : responseMessage,
        errors: fiscalFieldErrors(responseData?.errors) }
    }
  }

  unconfirmed(path, status) {
    return { success: false, status, endpointUnconfirmed: true, message: `Endpoint não confirmado: ${path}. O fluxo fiscal depende da disponibilização deste contrato na API.` }
  }
  listEvolutions(filters) { return this.request('get', '/faturamento/evolucoes', 'faturamento.evolucoes.view', filters) }
  createContext(payload) { return this.request('post', '/faturamento/evolucoes/contexto-nfse', 'nfse.preview', payload, true) }
  preview(payload = {}) {
    if (!payload.operation_id || !validOperationVersion(payload.version)) {
      return Promise.resolve({ success: false, code: 'invalid_preview_reference', message: 'Preview exige operação e versão válidas.' })
    }
    return this.request('post', '/nfse/preview', 'nfse.preview', {
      operation_id: payload.operation_id, version: Number(payload.version),
    })
  }
  emit(payload) { return this.request('post', '/nfse/emitir', 'nfse.emit', payload, true) }
  operation(id, { preview = false } = {}) {
    return this.request('get', `/nfse/operacoes/${encodeURIComponent(id)}`, preview ? 'nfse.preview' : 'nfse.documents.view')
  }
  documents(filters = {}) { return this.request('get', '/nfse/documentos', 'nfse.documents.view', filters) }
  configuration() { return this.request('get', '/nfse/configuracao', 'nfse.config.manage', {}, false, true) }
  updateConfiguration(payload) { return this.request('put', '/nfse/configuracao', 'nfse.config.manage', payload) }
  async cancel(id, payload) {
    const result = await this.request('post', `/nfse/${encodeURIComponent(id)}/cancelar`, 'nfse.cancel', payload, true)
    if (result.success && result.data && result.data.id == null) return { ...result, data: { ...result.data, id } }
    return result
  }
  // Listas auxiliares já usadas pelo App; não constituem evidência do contrato NFS-e.
  async agreements() {
    const result = await this.support('/agreement', 'agreements')
    return result
  }
  async support(path, key) {
    if (!this.empresa?.id || !userHasPermission(this.user, 'faturamento.evolucoes.view')) return { success: false, data: [], message: 'Sem permissão para consultar evoluções.' }
    try {
      const response = await this.http.get(path, { params: { empresa_id: this.empresa.id }, headers: { 'X-Empresa-Id': String(this.empresa.id) }, nfseRequest: true })
      return { success: response.status === 200 && response.data?.success !== false, data: response.data?.[key] || [] }
    } catch (error) {
      return { success: false, data: [], message: 'Não foi possível carregar os convênios.' }
    }
  }
}
